import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Grid, Box, Typography, IconButton, Chip, Switch, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import { getStages, createRegistrationStage, updateRegistrationStage, deleteRegistrationStage, reorderStages } from '../../../../Redux/Actions/action';
import ErrorHandler from '../../../../Utils/handler';
import ButtonContained from '../../../Common/ButtonContained';
import CommonTextField from '../../../Common/CommonTextField';

const StageManagement = () => {
  const dispatch = useDispatch();
  const [stages, setStages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [formData, setFormData] = useState({
    stageName: '',
    stageKey: '',
    stageNameGu: '',
    description: '',
    descriptionGu: '',
    statusOnComplete: 'Draft',
    isFinalStage: false,
    showInStepper: true,
    icon: '',
    color: '#3E4094',
  });

  // Fetch stages
  const fetchStages = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(getStages());
      const error = ErrorHandler(result);
      if (error) {
        setStages(result?.payload?.data?.stages || []);
      }
    } catch (error) {
      toast.error('Failed to fetch stages');
    } finally {
      dispatch(showLoader(false));
    }
  };

  useEffect(() => {
    fetchStages();
  }, []);

  // Handle form change
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Auto-generate stageKey from stageName
    if (field === 'stageName' && !selectedStage) {
      const key = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z_]/g, '');
      setFormData(prev => ({ ...prev, stageName: value, stageKey: key }));
    }
  };

  // Open dialog for add/edit
  const handleOpenDialog = (stage = null) => {
    if (stage) {
      setSelectedStage(stage);
      setFormData({
        stageName: stage.stageName || '',
        stageKey: stage.stageKey || '',
        stageNameGu: stage.stageNameGu || '',
        description: stage.description || '',
        descriptionGu: stage.descriptionGu || '',
        statusOnComplete: stage.statusOnComplete || 'Draft',
        isFinalStage: stage.isFinalStage || false,
        showInStepper: stage.showInStepper !== false,
        icon: stage.icon || '',
        color: stage.color || '#3E4094',
      });
    } else {
      setSelectedStage(null);
      setFormData({
        stageName: '',
        stageKey: '',
        stageNameGu: '',
        description: '',
        descriptionGu: '',
        statusOnComplete: 'Draft',
        isFinalStage: false,
        showInStepper: true,
        icon: '',
        color: '#3E4094',
      });
    }
    setDialogOpen(true);
  };

  // Save stage
  const handleSave = async () => {
    if (!formData.stageName || !formData.stageKey) {
      toast.error('Stage Name and Key are required');
      return;
    }

    dispatch(showLoader(true));
    try {
      let result;
      if (selectedStage) {
        result = await dispatch(updateRegistrationStage({
          stageKey: selectedStage.stageKey,
          body: formData
        }));
      } else {
        result = await dispatch(createRegistrationStage(formData));
      }
      
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message || 'Stage saved successfully');
        setDialogOpen(false);
        fetchStages();
      }
    } catch (error) {
      toast.error('Failed to save stage');
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Delete stage
  const handleDelete = async () => {
    if (!selectedStage) return;

    dispatch(showLoader(true));
    try {
      const result = await dispatch(deleteRegistrationStage({
        stageKey: selectedStage.stageKey,
        body: {}
      }));
      
      const error = ErrorHandler(result);
      if (error) {
        toast.success('Stage deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedStage(null);
        fetchStages();
      }
    } catch (error) {
      toast.error('Failed to delete stage');
    } finally {
      dispatch(showLoader(false));
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Registration Stage Management
        </Typography>
        <ButtonContained
          text="Add Stage"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        />
      </Box>

      {/* Info Box */}
      <Box sx={{ bgcolor: '#f0f4ff', p: 2, borderRadius: 2, mb: 3 }}>
        <Typography variant="body2" color="textSecondary">
          Configure dynamic registration stages for user onboarding. Each stage represents a step in the 
          member registration process. Stages can be reordered by drag and drop.
        </Typography>
      </Box>

      {/* Stages Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={50}>#</TableCell>
              <TableCell>Stage Name</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Status on Complete</TableCell>
              <TableCell align="center">In Stepper</TableCell>
              <TableCell align="center">Final Stage</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No stages configured. Click "Add Stage" to create one.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              stages.map((stage, index) => (
                <TableRow key={stage._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIndicatorIcon sx={{ color: '#ccc', cursor: 'grab' }} />
                      {stage.stageNumber}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>{stage.stageName}</Typography>
                    <Typography variant="caption" color="textSecondary">{stage.stageNameGu}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={stage.stageKey} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={stage.statusOnComplete} 
                      size="small" 
                      color={stage.statusOnComplete === 'Approved' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch checked={stage.showInStepper} disabled size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Switch checked={stage.isFinalStage} disabled size="small" color="warning" />
                  </TableCell>
                  <TableCell align="center">
                    <Switch checked={stage.isActive} disabled size="small" color="success" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(stage)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setSelectedStage(stage);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedStage ? 'Edit Stage' : 'Add New Stage'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <CommonTextField
                text="Stage Name (English)"
                placeholder="Enter stage name"
                value={formData.stageName}
                onChange={(e) => handleChange('stageName', e.target.value)}
                valid={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CommonTextField
                text="Stage Name (Gujarati)"
                placeholder="સ્ટેજનું નામ દાખલ કરો"
                value={formData.stageNameGu}
                onChange={(e) => handleChange('stageNameGu', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CommonTextField
                text="Stage Key"
                placeholder="stage_key"
                value={formData.stageKey}
                onChange={(e) => handleChange('stageKey', e.target.value)}
                valid={true}
                disabled={!!selectedStage}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CommonTextField
                text="Status on Complete"
                placeholder="Draft"
                value={formData.statusOnComplete}
                onChange={(e) => handleChange('statusOnComplete', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CommonTextField
                text="Description (English)"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CommonTextField
                text="Description (Gujarati)"
                placeholder="વર્ણન દાખલ કરો"
                value={formData.descriptionGu}
                onChange={(e) => handleChange('descriptionGu', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>Show in Stepper</Typography>
                <Switch
                  checked={formData.showInStepper}
                  onChange={(e) => handleChange('showInStepper', e.target.checked)}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>Is Final Stage</Typography>
                <Switch
                  checked={formData.isFinalStage}
                  onChange={(e) => handleChange('isFinalStage', e.target.checked)}
                  color="warning"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <CommonTextField
                text="Icon"
                placeholder="icon_name"
                value={formData.icon}
                onChange={(e) => handleChange('icon', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ButtonContained text="Cancel" bgColor="#f5f5f5" color="#333" onClick={() => setDialogOpen(false)} />
          <ButtonContained text={selectedStage ? 'Update' : 'Create'} onClick={handleSave} />
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete stage "{selectedStage?.stageName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ButtonContained text="Cancel" bgColor="#f5f5f5" color="#333" onClick={() => setDeleteDialogOpen(false)} />
          <ButtonContained text="Delete" bgColor="#d32f2f" onClick={handleDelete} />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StageManagement;
